import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  FiChevronLeft, FiDownload, FiCheck, FiZap, FiMessageSquare,
  FiMail, FiCalendar, FiDollarSign
} from 'react-icons/fi';
import api from '../api';

export default function BillingDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [billing, setBilling] = useState(null);
  const [usageDetails, setUsageDetails] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadBilling = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/api/billing/${id}`);
      setBilling(res.data.billing);
      setUsageDetails(res.data.usageDetails || []);
    } catch (error) {
      console.error('Failed to load billing:', error);
      alert('정산 내역을 불러오는데 실패했습니다.');
      navigate('/billing');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  const handleDownloadExcel = async () => {
    try {
      const res = await api.get(`/api/billing/${id}/hometax-excel`, {
        responseType: 'blob'
      });

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hometax_${billing.billingMonth}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download excel error:', error);
      alert('엑셀 다운로드에 실패했습니다.');
    }
  };

  const handleConfirm = async () => {
    if (!window.confirm('정산을 확정하시겠습니까? 확정 후에는 수정할 수 없습니다.')) {
      return;
    }

    try {
      await api.post(`/api/billing/${id}/confirm`);
      alert('정산이 확정되었습니다.');
      loadBilling();
    } catch (error) {
      console.error('Confirm billing error:', error);
      alert(error.response?.data?.error || '정산 확정에 실패했습니다.');
    }
  };

  const getServiceIcon = (type) => {
    switch (type) {
      case 'push':
        return FiZap;
      case 'alimtalk':
        return FiMessageSquare;
      case 'sms':
        return FiMail;
      default:
        return FiDollarSign;
    }
  };

  const getServiceName = (type) => {
    const names = {
      push: '푸시 알림',
      alimtalk: '알림톡',
      sms: 'SMS'
    };
    return names[type] || type;
  };

  const getServiceColor = (type) => {
    const colors = {
      push: '#3B82F6',
      alimtalk: '#10B981',
      sms: '#F59E0B'
    };
    return colors[type] || '#6B7280';
  };

  if (loading) {
    return <div className="loading">로딩 중...</div>;
  }

  if (!billing) {
    return null;
  }

  const breakdown = billing.usageBreakdown;
  const totalCount =
    breakdown.push.count + breakdown.alimtalk.count + breakdown.sms.count;

  return (
    <div className="billing-detail-page">
      <div className="page-header">
        <div>
          <button className="btn-back" onClick={() => navigate('/billing')}>
            <FiChevronLeft />
          </button>
          <div>
            <h2>{billing.billingMonth} 정산 내역</h2>
            <p>상세 사용 내역 및 현금영수증 발급</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={handleDownloadExcel}>
            <FiDownload />
            홈택스 엑셀
          </button>
          {billing.status === 'generated' && (
            <button className="btn-primary" onClick={handleConfirm}>
              <FiCheck />
              정산 확정
            </button>
          )}
        </div>
      </div>

      {/* 정산 요약 */}
      <div className="billing-summary-card">
        <div className="summary-header">
          <div>
            <h3>정산 요약</h3>
            <span className={`status-badge ${billing.status}`}>
              {billing.status === 'confirmed' ? '확정' : '생성'}
            </span>
          </div>
          <div className="summary-date">
            <FiCalendar />
            {billing.billingMonth}
          </div>
        </div>

        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-label">플랜</span>
            <span className="summary-value">{billing.planType.toUpperCase()}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">총 발송</span>
            <span className="summary-value">{totalCount.toLocaleString()}건</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">사용 금액</span>
            <span className="summary-value highlight">
              {billing.totalUsageAmount.toLocaleString()}원
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">생성일</span>
            <span className="summary-value">
              {format(new Date(billing.generatedAt), 'yyyy-MM-dd HH:mm')}
            </span>
          </div>
        </div>
      </div>

      {/* 서비스별 사용 내역 */}
      <div className="section">
        <h3>서비스별 사용 현황</h3>
        <div className="service-breakdown-grid">
          {Object.entries(breakdown).map(([serviceType, data]) => {
            const Icon = getServiceIcon(serviceType);
            const color = getServiceColor(serviceType);

            return (
              <div key={serviceType} className="service-breakdown-card">
                <div className="service-header">
                  <div className="service-icon" style={{ backgroundColor: `${color}20`, color }}>
                    <Icon size={24} />
                  </div>
                  <span className="service-name">{getServiceName(serviceType)}</span>
                </div>
                <div className="service-stats">
                  <div className="service-stat">
                    <span className="stat-label">총 발송</span>
                    <span className="stat-value">{data.count.toLocaleString()}건</span>
                  </div>
                  {data.free > 0 && (
                    <div className="service-stat">
                      <span className="stat-label">무료</span>
                      <span className="stat-value free">{data.free.toLocaleString()}건</span>
                    </div>
                  )}
                  {data.paid > 0 && (
                    <div className="service-stat">
                      <span className="stat-label">유료</span>
                      <span className="stat-value paid">{data.paid.toLocaleString()}건</span>
                    </div>
                  )}
                  <div className="service-stat total">
                    <span className="stat-label">비용</span>
                    <span className="stat-value">{data.amount.toLocaleString()}원</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 상세 사용 내역 */}
      <div className="section">
        <h3>상세 사용 내역 (최근 100건)</h3>
        {usageDetails.length === 0 ? (
          <div className="empty-state">
            <p>사용 내역이 없습니다.</p>
          </div>
        ) : (
          <div className="usage-table">
            <table>
              <thead>
                <tr>
                  <th>일시</th>
                  <th>서비스</th>
                  <th>수량</th>
                  <th>무료</th>
                  <th>유료</th>
                  <th>금액</th>
                  <th>설명</th>
                </tr>
              </thead>
              <tbody>
                {usageDetails.map((usage) => (
                  <tr key={usage._id}>
                    <td>{format(new Date(usage.usedAt), 'MM-dd HH:mm')}</td>
                    <td>
                      <span className="service-tag" style={{ color: getServiceColor(usage.serviceType) }}>
                        {getServiceName(usage.serviceType)}
                      </span>
                    </td>
                    <td>{usage.quantity}건</td>
                    <td className="free">{usage.freeCount}건</td>
                    <td className="paid">{usage.paidCount}건</td>
                    <td className="amount">{usage.usedAmount.toLocaleString()}원</td>
                    <td className="description">{usage.description || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 현금영수증 안내 */}
      <div className="info-box">
        <FiDollarSign />
        <div>
          <h4>현금영수증 발급 안내</h4>
          <ul>
            <li>홈택스 엑셀 파일을 다운로드하여 국세청 홈택스에서 현금영수증을 발급할 수 있습니다</li>
            <li>발급 기한: 사용월의 다음 달 10일까지</li>
            <li>문의: support@edustaff.co.kr</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
